import React, { useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { QuoteCartProvider } from './context/QuoteCartContext';
import { Layout } from './components/layout/Layout';
import { HomePage } from './pages/HomePage';
import { CatalogPage } from './pages/CatalogPage';
import { ProductPage } from './pages/ProductPage';
import { QuotePage } from './pages/QuotePage';
import { UseCasesPage } from './pages/UseCasesPage';
import { LoginPage, RegisterPage, AuthCallback } from './pages/AuthPages';
import { DashboardPage } from './pages/DashboardPage';
import { ContactPage, LegalPage } from './pages/ContactPage';
import { SampleRequestPage } from './pages/SampleRequestPage';
import { AdminPage } from './pages/AdminPage';
import { seedData } from './lib/api';
import { useEffect } from 'react';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// App Router with Auth Callback Detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/catalogue" element={<Layout><CatalogPage /></Layout>} />
      <Route path="/catalogue/:category" element={<Layout><CatalogPage /></Layout>} />
      <Route path="/catalogue/:category/:subcategory" element={<Layout><CatalogPage /></Layout>} />
      <Route path="/produit/:slug" element={<Layout><ProductPage /></Layout>} />
      <Route path="/devis" element={<Layout><QuotePage /></Layout>} />
      <Route path="/cas-usage" element={<Layout><UseCasesPage /></Layout>} />
      <Route path="/cas-usage/:slug" element={<Layout><UseCasesPage /></Layout>} />
      <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="/demande-echantillon" element={<Layout><SampleRequestPage /></Layout>} />
      <Route path="/blog" element={<Layout><ComingSoonPage title="Blog" /></Layout>} />
      
      {/* Legal Pages */}
      <Route path="/mentions-legales" element={<Layout><LegalPage type="mentions-legales" /></Layout>} />
      <Route path="/cgv" element={<Layout><LegalPage type="cgv" /></Layout>} />
      <Route path="/confidentialite" element={<Layout><LegalPage type="confidentialite" /></Layout>} />
      <Route path="/cookies" element={<Layout><LegalPage type="cookies" /></Layout>} />
      
      {/* Admin Route */}
      <Route path="/admin" element={<AdminPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<Layout><LoginPage /></Layout>} />
      <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><DashboardPage /></Layout>
        </ProtectedRoute>
      } />
      
      {/* 404 */}
      <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
    </Routes>
  );
}

// Coming Soon placeholder
function ComingSoonPage({ title }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
      <div>
        <h1 className="text-3xl font-bold mb-4 font-['Outfit']">{title}</h1>
        <p className="text-muted-foreground">Coming soon...</p>
      </div>
    </div>
  );
}

// 404 Page
function NotFoundPage() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
      <div>
        <h1 className="text-6xl font-bold mb-4 font-['Outfit']">404</h1>
        <p className="text-muted-foreground mb-8">Page non trouvée</p>
        <a href="/" className="text-[#D4AF37] hover:underline">Retour à l'accueil</a>
      </div>
    </div>
  );
}

// Seed data on first load
function DataSeeder() {
  const hasSeeded = useRef(false);
  
  useEffect(() => {
    if (hasSeeded.current) return;
    hasSeeded.current = true;
    
    seedData().catch(err => {
      // Silently fail if already seeded
      console.log('Seed:', err.response?.data?.message || 'Data ready');
    });
  }, []);
  
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <QuoteCartProvider>
            <DataSeeder />
            <AppRouter />
          </QuoteCartProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
